/**
 * Days-to-sell (DOM) model output: mini line for browse cards / map, full chart for reports.
 * Expects API fields: dom_predicted, dom_pi_lower, dom_pi_upper, dom_90_lower, dom_90_upper, dom_std.
 */

function num(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** One-line summary for PropertyPredictionCard / map popup */
export function DaysToSellMini({ prediction, className = '' }) {
  const p = num(prediction?.dom_predicted)
  if (p == null || p <= 0) return null
  const lo = num(prediction?.dom_pi_lower)
  const hi = num(prediction?.dom_pi_upper)
  return (
    <p
      className={className}
      title="Model estimate of days to sell at the current list price (not a guarantee)."
    >
      <span className="font-semibold text-primary">Est. time to sell:</span>{' '}
      {Math.round(p)} days
      {lo != null && hi != null && (
        <span className="text-slate-500 dark:text-slate-400 font-normal">
          {' '}
          ({Math.round(lo)}–{Math.round(hi)} days, 80% range)
        </span>
      )}
      <span className="ml-1.5 inline-flex items-center px-1 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-indigo-100 dark:bg-indigo-900/40 text-indigo-500 dark:text-indigo-400 leading-none align-middle">Alpha</span>
    </p>
  )
}

/**
 * Full section: title + bell-style chart (days on x-axis) + caption.
 * Mirrors PredictionCard price distribution styling.
 */
export default function DaysToSellSection({ prediction }) {
  const domPred = num(prediction?.dom_predicted)
  if (domPred == null || domPred <= 0) return null

  const piLo = num(prediction?.dom_pi_lower)
  const piHi = num(prediction?.dom_pi_upper)
  const p90Lo = num(prediction?.dom_90_lower)
  const p90Hi = num(prediction?.dom_90_upper)

  const use90 = p90Lo != null && p90Hi != null
  const use80 = piLo != null && piHi != null

  const dataMin = use90 ? p90Lo : use80 ? piLo : domPred * 0.5
  const dataMax = use90 ? p90Hi : use80 ? piHi : domPred * 1.5
  const minVal = Math.min(dataMin, domPred)
  const maxVal = Math.max(dataMax, domPred)
  const padding = Math.max(2, (maxVal - minVal) * 0.12)
  const rangeMin = Math.max(1, minVal - padding)
  const rangeMax = maxVal + padding
  const totalRange = rangeMax - rangeMin || 1

  const dayToX = (d) => 10 + ((d - rangeMin) / totalRange) * 280
  const predX = dayToX(domPred)
  const pi80LoX = use80 ? dayToX(piLo) : null
  const pi80HiX = use80 ? dayToX(piHi) : null
  const pi90LoX = use90 ? dayToX(p90Lo) : null
  const pi90HiX = use90 ? dayToX(p90Hi) : null

  const peakY = 25
  const baseY = 100
  const bandLeft = use90 ? pi90LoX : use80 ? pi80LoX : predX - 40
  const bandRight = use90 ? pi90HiX : use80 ? pi80HiX : predX + 40
  const curveWidth = Math.max(30, bandRight - bandLeft)
  let startX = Math.max(10, bandLeft - curveWidth * 0.25)
  let endX = Math.min(290, bandRight + curveWidth * 0.25)
  const sigma = Math.max(8, (endX - startX) / 4)

  const bellPath = (() => {
    const points = []
    const steps = 50
    for (let i = 0; i <= steps; i++) {
      const x = startX + (endX - startX) * (i / steps)
      const z = (x - predX) / sigma
      const y = baseY - (baseY - peakY) * Math.exp(-0.5 * z * z)
      points.push(`${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    }
    return points.join(' ')
  })()

  const fmtDay = (d) => `${Math.round(d)}d`

  return (
    <div className="mb-6 break-inside-avoid">
      <div className="flex items-center justify-center sm:justify-start mb-3">
        <span className="text-slate-600 dark:text-slate-300 text-sm font-semibold">
          Expected time on market (at list price)
        </span>
      </div>
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-xl">schedule</span>
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Days to sell — distribution</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-indigo-100 dark:bg-indigo-900/40 text-indigo-500 dark:text-indigo-400 leading-none">Alpha</span>
        </div>
        <svg viewBox="0 0 300 150" className="w-full h-auto max-h-[200px]">
          <defs>
            <linearGradient id="dom90Grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e0e7ff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#e0e7ff" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="dom80Grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#c7d2fe" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#c7d2fe" stopOpacity="0.35" />
            </linearGradient>
          </defs>
          {use90 && (
            <rect
              x={pi90LoX}
              y={peakY}
              width={pi90HiX - pi90LoX}
              height={baseY - peakY}
              fill="url(#dom90Grad)"
              rx="2"
            />
          )}
          {use80 && (
            <rect
              x={pi80LoX}
              y={peakY}
              width={pi80HiX - pi80LoX}
              height={baseY - peakY}
              fill="url(#dom80Grad)"
              rx="2"
            />
          )}
          <path
            d={bellPath}
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
            className="dark:stroke-indigo-400"
          />
          <line x1="10" y1={baseY} x2="290" y2={baseY} stroke="#94a3b8" strokeWidth="1" />
          <line
            x1={predX}
            y1={peakY - 5}
            x2={predX}
            y2={baseY}
            stroke="#10b981"
            strokeWidth="2"
            strokeDasharray="4,2"
          />
          <circle cx={predX} cy={peakY - 5} r="6" fill="#10b981" />
          <text
            x={predX}
            y="14"
            textAnchor="middle"
            style={{ fontSize: '10px', fontWeight: 'bold', fill: '#059669' }}
          >
            {fmtDay(domPred)}
          </text>
          {use80 && (
            <>
              <text
                x={pi80LoX}
                y="112"
                textAnchor="middle"
                style={{ fontSize: '9px', fontWeight: '600', fill: '#6366f1' }}
              >
                {fmtDay(piLo)}
              </text>
              <text
                x={pi80HiX}
                y="112"
                textAnchor="middle"
                style={{ fontSize: '9px', fontWeight: '600', fill: '#6366f1' }}
              >
                {fmtDay(piHi)}
              </text>
            </>
          )}
          <g transform="translate(60, 132)">
            {use90 && (
              <>
                <rect x="0" y="0" width="14" height="10" fill="#e0e7ff" rx="2" />
                <text x="18" y="8" style={{ fontSize: '9px', fill: '#64748b' }}>
                  90%
                </text>
              </>
            )}
            {use80 && (
              <>
                <rect x={use90 ? 48 : 0} y="0" width="14" height="10" fill="#c7d2fe" rx="2" />
                <text x={use90 ? 66 : 18} y="8" style={{ fontSize: '9px', fill: '#64748b' }}>
                  80%
                </text>
              </>
            )}
          </g>
        </svg>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 text-center leading-relaxed">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Point estimate (days)
          </span>
          {' · '}
          Bands reflect model uncertainty; not a guarantee of sale timing.
        </p>
      </div>
    </div>
  )
}
